import { HStack } from '@chakra-ui/react';
import { SEE_NOTIFICATIONS_ROLES } from '@components/cockpit/cockpit.types';
import { MenuIconBar } from '@components/common/menu/MenuIconBar';
import { buildMenuItems, MenuBuilderContext, MenuPopupType } from '@components/map/controls/menus/menuBuilders';
import { useMapContext } from '@components/map/MapContext';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useWithDispatch } from '@hooks/useWithDispatch';
import {
  hasAnyActionRoleOnSensors,
  hasRole,
  hasSensorRoleFromActiveProfile,
  selectActiveUser,
} from '@redux/authent/authent.selectors';
import { selectMapLayerById, selectMapLayers } from '@redux/config/config.selectors';
import {
  closePopupByMapIdAndType,
  switchDrawModeByMapId,
  updateDrawZoneTypeSelected,
  updateMenuNavigationStatus,
  updatePopupByMapId,
} from '@redux/maps/maps.reducer';
import { selectMapDrawModeByMapId, selectMapNavigationOptions, selectPopupsByMapId } from '@redux/maps/maps.selectors';
import { selectCurrentBaseLayerId, selectOverlaysKey } from '@redux/settings/settings.selectors';
import { RootState } from '@redux/store';
import { usePatchBaseLayerMutation, usePatchOverlaysMutation } from '@services/config/setting.api';
import { findPopupByType, isOpenedPopupByType } from '@utils/popup.utils';
import { useCallback } from 'react';

import { RoleEnum } from '@/types/authent/roles.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { Zone2DTypeEnum, Zone3DTypeEnum } from '@/types/config/config.types';
import { MapIdEnum } from '@/types/map.types';
import { MenuItemOption, MenuItemOptionIcons, MenuItemsIcons } from '@/types/menu.types';
import { PopupTypeEnum } from '@/types/popup.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';

export function ToolMenu() {
  const user = useAppSelector(selectActiveUser);
  const currentBaseLayerId = useAppSelector(selectCurrentBaseLayerId);
  const currentBaseLayer = useAppSelector((state: RootState) => selectMapLayerById(state, currentBaseLayerId));
  const currentOverlays = useAppSelector(selectOverlaysKey);
  const [updateBaseLayer] = usePatchBaseLayerMutation();
  const [updateOverlays] = usePatchOverlaysMutation();
  const { mapId, sidebarOptions, isMeasureActive, measurePoints, setIsMeasureActive } = useMapContext();
  const isMapDrawModeOn = useSelectorWithMapId(selectMapDrawModeByMapId);
  const dispatchSwitchDrawModeByMapId = useWithDispatch(switchDrawModeByMapId);
  const selectedOptions = useSelectorWithMapId(selectMapNavigationOptions) ?? [];
  const dispatchSelectedOptions = useWithDispatch(updateMenuNavigationStatus);
  const dispatchZoneType = useWithDispatch(updateDrawZoneTypeSelected);

  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const closePopupByType = useWithDispatch(closePopupByMapIdAndType);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const canCreateMapLayer = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_MAP_LAYER));
  const canAccessFlightPlans = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ACCESS_FLIGHT_PLAN));
  const canAccessNeutral = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.ACCESS, sensorFamily: SensorFamilyEnum.NEUTRAL }),
  );
  const canSeeNotifications = useAppSelector((state: RootState) =>
    hasAnyActionRoleOnSensors(state, SEE_NOTIFICATIONS_ROLES),
  );

  const mapLayers = useAppSelector(selectMapLayers);

  const toggleMeasureTool = useCallback(() => {
    const isPopupOpen = isOpenedPopupByType(popups, PopupTypeEnum.MEASURE_INFO);
    const hasDrawing = measurePoints.length > 0;

    const openPopup = () =>
      updatePopup({
        mapId,
        popup: { type: PopupTypeEnum.MEASURE_INFO, position: { x: 0, y: 0 }, data: null },
      });

    const closePopup = () => {
      setIsMeasureActive(false);
      closePopupByType({ mapId, type: PopupTypeEnum.MEASURE_INFO });
    };

    if (!hasDrawing) {
      if (!isMeasureActive) {
        setIsMeasureActive(true);
        openPopup();
      } else {
        closePopup();
      }
    } else {
      !isPopupOpen ? openPopup() : closePopup();
    }
  }, [popups, measurePoints.length, isMeasureActive, setIsMeasureActive, updatePopup, mapId, closePopupByType]);

  const handleClick = (option: string, value: MenuItemOptionIcons | MenuItemOption | undefined, depth: number) => {
    let temp = structuredClone(selectedOptions).slice(0, depth + 1);
    if (isMapDrawModeOn) {
      dispatchSwitchDrawModeByMapId({ mapId, isDrawable: false });
    }
    // click on same place => revert navigation
    if (selectedOptions[depth] === option) {
      const isLastLevel = !value?.subMenuItems;

      if (isLastLevel && value?.onAction) {
        value.onAction();
      }
      dispatchSelectedOptions({ mapId, selectedOptions: temp.slice(0, depth) });
    } else {
      // else forward navigation
      temp = temp.toSpliced(depth, 1, option);

      if (option === 'maps' && currentBaseLayer) {
        temp.push(currentBaseLayer.code);
      }

      dispatchSelectedOptions({ mapId, selectedOptions: temp });
      // trigger all potential action linked to menu btn
      if (value?.onAction) {
        value.onAction();
      }
    }
  };

  const handleBaseLayerChanges = (baseLayer: number) => {
    if (baseLayer) {
      updateBaseLayer({ login: user.login, baseLayer });
    }
  };

  const handleOverlaysChanges = (overlay: number) => {
    const layersSet = new Set(currentOverlays);
    layersSet.has(overlay) ? layersSet.delete(overlay) : layersSet.add(overlay);

    updateOverlays({
      login: user.login,
      overlays: [...layersSet],
    });
  };

  const handleZoneCreateClick = (zoneType: Zone2DTypeEnum | Zone3DTypeEnum | undefined) => {
    if (isMeasureActive) {
      closePopupByType({ mapId, type: PopupTypeEnum.MEASURE_INFO });
    }
    dispatchSwitchDrawModeByMapId({ mapId, isDrawable: true });
    dispatchZoneType({ mapId, zoneType });
  };

  const handlePopupOpeningByType = (type: MenuPopupType) => {
    if (findPopupByType(popups, type)) {
      closePopupByType({ mapId, type });
    } else {
      updatePopup({
        mapId,
        popup: {
          type,
          position: { x: 400, y: 50 },
          data: null,
        },
      });
    }
  };

  const ctx: MenuBuilderContext = {
    mapLayers,
    currentBaseLayerId,
    currentOverlays,
    isCreateMapLayerSelected: isOpenedPopupByType(popups, PopupTypeEnum.MAP_LAYER),
    isCreatePoiSelected: isOpenedPopupByType(popups, PopupTypeEnum.POI),
    handleBaseLayerChanges,
    handleOverlaysChanges,
    handlePopupOpeningByType,
    isOpenedPopupByType,
    filterMode: mapId === MapIdEnum.STATISTICS ? 'statistics' : 'regular',
    canCreateMapLayer,
    canAccessFlightPlans,
    canSeeNotifications,
    canAccessNeutral,
    handleZoneCreateClick,
    popups,
    toggleMeasureTool,
  };

  const menuItems: MenuItemsIcons = buildMenuItems(sidebarOptions, ctx);

  return (
    <HStack gap={0} marginTop="76px" alignItems="flex-start">
      <MenuIconBar menuItems={menuItems} depth={0} selectedOptions={selectedOptions} handleClick={handleClick} />
    </HStack>
  );
}
